import {
  dataSourceService,
  ofrDocumentTemplateService
} from '.'
import {
  algorithmRunStatuses,
  riskReliefSmartFunctionTypes,
  workspaceTypes,
} from '../constants/constants'
import store from '../store'
import { selectRootCauseInclusions } from '../store/reducers/rootCauseInclusionReducer'
import {
  AlgorithmRun,
  AlgorithmRunBiomarkConfig,
  AlgorithmRunConfig,
  AlgorithmRunConformityCheckConfig,
  AlgorithmRunDelConfig,
  AlgorithmRunRegulatorySubmissionConfig,
  AlgorithmRunRiskReliefConfig,
  RiskReliefAlgorithmRun,
  WorkspaceConfig,
  WorkspaceDetails,
} from '../store/reducers/workspaceReducer'

const getBiomarkConfig = (
  algorithmRun: AlgorithmRun,
  selectedWorkspace: WorkspaceDetails
): AlgorithmRunBiomarkConfig | undefined => {
  return algorithmRun.config as AlgorithmRunBiomarkConfig
}

const getRegsubConfig = (
  algorithmRun: AlgorithmRun
): AlgorithmRunRegulatorySubmissionConfig | undefined => {
  return algorithmRun.config as AlgorithmRunRegulatorySubmissionConfig
}

const getDelConfig = (
  algorithmRun: AlgorithmRun
): AlgorithmRunDelConfig | undefined => {
  return algorithmRun.config as AlgorithmRunDelConfig
}

interface RiskReliefConfigParams {
  searchQueryId?: string
  selectedWorkspace?: WorkspaceDetails
  algorithmRun?: RiskReliefAlgorithmRun
}

const getRiskReliefConfig = async ({
  searchQueryId, // aka deviationId
  selectedWorkspace,
  algorithmRun,
}: RiskReliefConfigParams): Promise<
  AlgorithmRunRiskReliefConfig | undefined
> => {
  if (!searchQueryId || !selectedWorkspace) return undefined

  const workspaceConfig = selectedWorkspace.config as WorkspaceConfig
  const deviations = workspaceConfig?.riskReliefConfig?.deviations
  if (!deviations) return undefined

  const baseConfig = {
    deviationId: searchQueryId,
    deviations: deviations
      .filter((deviation) => deviation.searchQueryId === searchQueryId)
      .map((deviation) => ({
        id: deviation.searchResultId,
        urn: deviation?.urn || '',
      })),
  } as AlgorithmRunRiskReliefConfig

  if (
    algorithmRun?.smartFunctionType ===
    riskReliefSmartFunctionTypes.CAPA_GENERATION
  ) {
    const state = store.getState()
    const rootCauseInclusions = selectRootCauseInclusions(
      state,
      selectedWorkspace.id,
      algorithmRun.id
    )
    if (!rootCauseInclusions) return baseConfig

    return {
      ...baseConfig,
      rootCauseInclusions,
    }
  }

  return baseConfig
}

interface GetOfrQrConfigParams {
  selectedWorkspace: WorkspaceDetails
  documentObjectId?: string
}

const getOfrQrConfig = async ({
  selectedWorkspace,
  documentObjectId,
}: GetOfrQrConfigParams): Promise<
  AlgorithmRunConformityCheckConfig | undefined
> => {
  const { config, dataSource, algorithmRuns } = selectedWorkspace
  const ofrTemplateId = config?.ofrDocumentTemplatesId

  const baseOfrQrConfig: AlgorithmRunConformityCheckConfig = {
    documentTemplateName: config?.ofrDocumentTemplatesName,
    ofrTemplateId: ofrTemplateId,
    documentTemplateId: config?.ofrDocumentTemplates,
    documentObjectId,
    documentTargetId: undefined,
    comparisonRunId: undefined,
    iterationFilename: undefined,
  }

  if (!ofrTemplateId || !documentObjectId || !algorithmRuns) {
    return baseOfrQrConfig
  }

  // get most recent completed labQr run for the document else return undefined
  const comparisonRunId = algorithmRuns
    .slice()
    .reverse()
    .find(
      (run) =>
        (run.config as AlgorithmRunConformityCheckConfig)?.documentObjectId ===
          documentObjectId && run.runStatus === algorithmRunStatuses.COMPLETE
    )?.id

  try {
    const [latestIteration, ofrTemplate] = await Promise.all([
      dataSourceService.getLatestIteration({
        dataSourceId: dataSource.id,
        documentId: documentObjectId,
      }),
      ofrDocumentTemplateService.getOfrDocumentTemplate({
        documentId: ofrTemplateId,
      }),
    ])

    const latestIterationDocumentFileName = latestIteration.path
      .split('/')
      .pop()

    return {
      documentTemplateName:
        ofrTemplate?.title ?? config?.ofrDocumentTemplatesName,
      ofrTemplateId: ofrTemplate?.id ?? ofrTemplateId,
      documentTemplateId:
        ofrTemplate?.documentId ?? config?.ofrDocumentTemplates,
      documentObjectId,
      documentTargetId: latestIteration.id,
      comparisonRunId,
      iterationFilename: latestIterationDocumentFileName,
    }
  } catch (error) {
    return baseOfrQrConfig
  }
}

const getBaseAlgorithmConfig = (
  algorithmRun: AlgorithmRun,
  selectedWorkspace: WorkspaceDetails
) => {
  if (!algorithmRun || !selectedWorkspace) return undefined

  switch (selectedWorkspace.typeCode) {
    case workspaceTypes.BIOMARK:
      return getBiomarkConfig(algorithmRun, selectedWorkspace)
    case workspaceTypes.REGSUB:
      return getRegsubConfig(algorithmRun)
    case workspaceTypes.DEL:
      return getDelConfig(algorithmRun)
    default:
      return {}
  }
}

interface GetAlgorithmConfigParams {
  typeCode: string
  workspace: WorkspaceDetails
  searchQueryId?: string
  documentObjectId?: string
  algorithmRun?: RiskReliefAlgorithmRun | AlgorithmRun
}

const getAlgorithmConfig = async ({
  typeCode,
  workspace,
  searchQueryId,
  documentObjectId,
  algorithmRun,
}: GetAlgorithmConfigParams): Promise<AlgorithmRunConfig | undefined> => {
  switch (typeCode) {
    case workspaceTypes.RISK_REL:
      return getRiskReliefConfig({
        searchQueryId,
        selectedWorkspace: workspace,
        algorithmRun: algorithmRun as RiskReliefAlgorithmRun,
      })

    case workspaceTypes.OFR_QR: {
      const config = await getOfrQrConfig({
        selectedWorkspace: workspace,
        documentObjectId,
      })
      if (!config) {
        return undefined
      }
      return config
    }

    case workspaceTypes.REFAI:
      return {}

    default:
      if (!algorithmRun) return {}
      return getBaseAlgorithmConfig(algorithmRun, workspace)
  }
}

const smartFunctionActionService = {
  getBiomarkConfig,
  getRegsubConfig,
  getDelConfig,
  getRiskReliefConfig,
  getOfrQrConfig,
  getAlgorithmConfig,
}

export default smartFunctionActionService
